import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
  Code,
  useColorModeValue,
  HStack,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/v0/PageLayout';
import { NotificationService, PermissionStatus, ReceivedNotification } from '../../services/NotificationService';
import { calculateReminderTimes, scheduleTaskReminders } from '../../services/TaskNotificationHelper';
import { v4 as uuidv4 } from 'uuid';
import FCMTestPanel from '../../components/v0/notification/FCMTestPanel';

const NotificationTest: React.FC = () => {
  const { t } = useTranslation(['v0/common', 'v0/tasks', 'common', 'v0/notifications', 'v0/notificationTest']);
  const toast = useToast();

  // 获取通知服务实例
  const notificationService = NotificationService.getInstance();

  // 状态变量
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('default');
  const [platformInfo, setPlatformInfo] = useState<any>(null);
  const [notificationTitle, setNotificationTitle] = useState(t('v0/notifications:test.defaultTitle'));
  const [notificationBody, setNotificationBody] = useState(t('v0/notifications:test.defaultBody'));
  const [notificationDelay, setNotificationDelay] = useState(0);
  const [notificationSound, setNotificationSound] = useState(true);
  const [notificationLog, setNotificationLog] = useState<ReceivedNotification[]>([]);

  // 任务提醒测试状态
  const [taskTitle, setTaskTitle] = useState(t('v0/notifications:test.defaultTaskTitle'));
  const [taskNotes, setTaskNotes] = useState(t('v0/notifications:test.defaultTaskNotes'));
  const [reminderType, setReminderType] = useState<'absolute' | 'relative'>('relative');
  const [reminderTime, setReminderTime] = useState(''); // 绝对时间
  const [reminderOffset, setReminderOffset] = useState(30); // 相对偏移（秒）
  const [reminderChannel, setReminderChannel] = useState<'local' | 'fcm'>('local'); // 提醒渠道

  // 诊断信息状态
  const [diagnosticInfo, setDiagnosticInfo] = useState<any>(null);

  // 背景色
  const cardBg = useColorModeValue('white', 'gray.800');
  const logBg = useColorModeValue('gray.50', 'gray.700');

  // 初始化
  useEffect(() => {
    // 获取平台信息
    const info = notificationService.getPlatformInfo();
    setPlatformInfo(info);

    // 检查权限状态
    checkPermissionStatus();

    // 添加通知接收监听器
    const receivedSubscription = notificationService.addNotificationReceivedListener(
      (notification: ReceivedNotification) => {
        console.log('Notification received:', notification);
        setNotificationLog(prev => [notification, ...prev]);
        toast({
          title: t('v0/notifications:toast.receivedTitle'),
          description: `${notification.title}: ${notification.body}`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    );

    // 添加通知操作监听器
    const actionSubscription = notificationService.addNotificationActionListener(
      (notification: ReceivedNotification) => {
        console.log('Notification action:', notification);
        setNotificationLog(prev => [{
          ...notification,
          title: `[ACTION] ${notification.title}`
        }, ...prev]);
        toast({
          title: t('v0/notifications:toast.actionTitle'),
          description: t('v0/notifications:toast.actionDescription', { action: notification.actionId || t('v0/notifications:actions.click') }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    );

    // 清理函数
    return () => {
      receivedSubscription.remove();
      actionSubscription.remove();
    };
  }, []);

  // 检查权限状态
  const checkPermissionStatus = async () => {
    try {
      const status = await notificationService.checkPermission();
      console.log('[NotificationTest] Checked permission status:', status);
      setPermissionStatus(status);
    } catch (error) {
      console.error('Error checking permission:', error);
      toast({
        title: t('v0/notifications:toast.checkPermissionFailedTitle'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 请求权限按钮的处理逻辑 (修改后)
  const handleRequestPermissionClick = async () => {
    // 先重新检查一遍最新状态
    let currentStatus = await notificationService.checkPermission();
    setPermissionStatus(currentStatus); // 更新 UI 状态

    console.log('[NotificationTest] Requesting permission, current status:', currentStatus);

    // 根据不同平台和状态处理
    if (platformInfo?.isNative && platformInfo.os === 'android') {
      // Android 原生应用: 总是尝试请求，即使是 denied 状态
      if (currentStatus !== 'granted') { // 如果不是 granted，就尝试请求
        try {
          console.log('[NotificationTest] Attempting to request permission via Capacitor Plugin (even if denied)...');
          // **关键：总是尝试调用 Capacitor 插件请求权限**
          const result = await notificationService.requestPermission();
          console.log('[NotificationTest] Permission request attempt result:', result); // 结果可能是 denied
          // 请求后再次检查状态并更新
          currentStatus = await notificationService.checkPermission();
          setPermissionStatus(currentStatus);

          if (currentStatus === 'denied') {
            // 如果请求后状态仍然是 denied，提示用户去设置
            toast({
              title: t('v0/notifications:toast.permissionDeniedTitle'),
              description: t('v0/notifications:toast.permissionDeniedDescription'),
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
          } else {
            // 对于 prompt/default 变成 granted 的情况，或已经是 granted 的情况
            toast({
              title: t('v0/notifications:toast.permissionStatusTitle'),
              description: t('v0/notifications:toast.permissionStatusDescription', { status: getPermissionText(currentStatus) }),
              status: currentStatus === 'granted' ? 'success' : 'info', // 可能还是 prompt
              duration: 3000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error('[NotificationTest] Error requesting permission on Android:', error);
          toast({
            title: t('v0/notifications:toast.requestPermissionFailedTitle'),
            description: error instanceof Error ? error.message : t('v0/notifications:toast.cannotRequestPermission'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      } else { // 已经是 granted
        toast({
          title: t('v0/notifications:toast.permissionGrantedTitle'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } else if (platformInfo?.isElectron && currentStatus === 'granted') {
      // Electron on macOS: 检查系统设置
      notificationService.openSystemSettings();
      toast({
        title: t('v0/notifications:toast.checkSystemSettingsTitle'),
        description: t('v0/notifications:toast.checkSystemSettingsDescription'),
        status: 'info',
        duration: 7000,
        isClosable: true,
      });
    } else if (currentStatus === 'denied') {
        // 其他平台（Web, iOS denied, Electron denied）
        if (platformInfo?.isElectron) {
          notificationService.openSystemSettings(); // 尝试打开设置
        }
        toast({
          title: t('v0/notifications:toast.permissionDeniedTitle'),
          description: t('v0/notifications:toast.manualPermissionRequired'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
    } else if (currentStatus === 'prompt' || currentStatus === 'default') {
      // Web 平台首次请求 或 iOS 首次请求
      try {
        console.log('[NotificationTest] Requesting permission (Web/iOS prompt)...');
        const status = await notificationService.requestPermission();
        setPermissionStatus(status);
        toast({
          title: t('v0/notifications:toast.permissionStatusTitle'),
          description: t('v0/notifications:toast.permissionStatusDescription', { status: getPermissionText(status) }),
          status: status === 'granted' ? 'success' : 'warning',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error requesting permission:', error);
        toast({
          title: t('v0/notifications:toast.requestPermissionFailedTitle'),
          description: error instanceof Error ? error.message : t('common:errors.unknown'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } else { // granted (non-Android/Electron)
      toast({
        title: t('v0/notifications:toast.permissionGrantedTitle'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 发送测试通知
  const sendTestNotification = async () => {
    try {
      // 随机ID
      const id = Math.floor(Math.random() * 100000).toString();

      // 基本通知选项
      const options = {
        id,
        title: notificationTitle,
        body: notificationBody,
        sound: notificationSound,
        extra: {
          testData: 'test-value',
          timestamp: new Date().toISOString(),
          type: 'local_test'
        }
      };

      // 手动添加到日志（因为本地通知可能不会触发接收监听器）
      const logEntry: ReceivedNotification = {
        id,
        title: notificationTitle,
        body: notificationBody,
        extra: options.extra
      };

      // 如果有延迟，使用schedule
      if (notificationDelay > 0) {
        const scheduleTime = new Date(Date.now() + notificationDelay * 1000);
        Object.assign(options, {
          schedule: {
            at: scheduleTime
          }
        });

        await notificationService.scheduleLocalNotification(options);

        // 添加到日志（标记为已调度）
        setNotificationLog(prev => [{
          ...logEntry,
          title: `[SCHEDULED] ${logEntry.title}`,
          body: `${logEntry.body} (将在 ${scheduleTime.toLocaleTimeString()} 发送)`
        }, ...prev]);

        toast({
          title: t('v0/notifications:toast.notificationScheduledTitle'),
          description: t('v0/notifications:toast.notificationScheduledDescription', { seconds: notificationDelay }),
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // 即时发送
        await notificationService.scheduleLocalNotification(options);

        // 添加到日志（标记为已发送）
        setNotificationLog(prev => [{
          ...logEntry,
          title: `[SENT] ${logEntry.title}`
        }, ...prev]);

        toast({
          title: t('v0/notifications:toast.notificationSentTitle'),
          description: t('v0/notifications:toast.notificationSentDescription'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: t('v0/notifications:toast.sendNotificationFailedTitle'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 创建任务提醒测试
  const sendTaskReminder = async () => {
    try {
      if (reminderChannel === 'local') {
        // 本地通知渠道 - 直接发送本地通知
        const scheduleTime = reminderType === 'absolute'
          ? new Date(reminderTime || new Date().getTime() + 10000)
          : new Date(Date.now() + reminderOffset * 1000);

        const options = {
          id: `task-reminder-${uuidv4()}`,
          title: `📋 ${taskTitle}`,
          body: taskNotes || t('v0/notifications:test.defaultTaskReminderBody'),
          sound: true,
          schedule: {
            at: scheduleTime
          },
          extra: {
            type: 'task_reminder',
            taskId: uuidv4(),
            timestamp: new Date().toISOString(),
          }
        };

        await notificationService.scheduleLocalNotification(options);

        // 添加到日志
        setNotificationLog(prev => [{
          id: options.id,
          title: `[TASK REMINDER] ${options.title}`,
          body: `${options.body} (计划在 ${scheduleTime.toLocaleString()} 提醒)`,
          extra: options.extra
        }, ...prev]);

        toast({
          title: t('v0/notifications:toast.taskReminderScheduledTitle'),
          description: t('v0/notifications:toast.taskReminderScheduledLocalDescription', {
            time: scheduleTime.toLocaleString(),
            channel: t('v0/notifications:channels.local')
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // FCM 渠道 - 使用原有的任务提醒调度功能
        const testTaskId = uuidv4();
        const now = new Date();

        let testTask: any = {
          id: testTaskId,
          title: taskTitle,
          notes: taskNotes,
          reminders: [{
            action: 'DISPLAY',
            methods: ['ALERT'],
            trigger: reminderType === 'absolute'
              ? {
                  type: 'ABSOLUTE',
                  absoluteTime: reminderTime || new Date(now.getTime() + 10000).toISOString()
                }
              : {
                  type: 'RELATIVE',
                  related: 'START',
                  offsetSeconds: reminderOffset
                }
          }],
          startAt: now.toISOString(),
        };

        // 调用任务提醒通知调度功能
        await scheduleTaskReminders([testTask], t);

        toast({
          title: t('v0/notifications:toast.taskReminderScheduledTitle'),
          description: reminderType === 'absolute'
            ? t('v0/notifications:toast.taskReminderScheduledAbsoluteDescription', {
                time: new Date(reminderTime).toLocaleString(),
                channel: t('v0/notifications:channels.fcm')
              })
            : t('v0/notifications:toast.taskReminderScheduledRelativeDescription', {
                seconds: reminderOffset,
                channel: t('v0/notifications:channels.fcm')
              }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error scheduling task reminder:', error);
      toast({
        title: t('v0/notifications:toast.taskReminderScheduleFailedTitle'),
        description: error instanceof Error ? error.message : t('common:errors.unknown'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 收集诊断信息
  const collectDiagnosticInfo = async () => {
    console.log('[NotificationTest] Starting diagnostic info collection...');

    try {
      const info: any = {
        timestamp: new Date().toISOString(),
        platform: platformInfo,
        permission: permissionStatus,
        userAgent: navigator.userAgent,
        serviceWorker: 'serviceWorker' in navigator,
        notifications: 'Notification' in window,
        pushManager: 'PushManager' in window,
      };

      console.log('[NotificationTest] Basic info collected:', info);

      // 检查 Service Worker 状态
      if ('serviceWorker' in navigator) {
        try {
          console.log('[NotificationTest] Checking Service Worker...');
          const registrations = await navigator.serviceWorker.getRegistrations();
          info.serviceWorkerRegistrations = registrations.length;

          // 简化 serviceWorkerReady 检查，避免可能的挂起
          if (registrations.length > 0) {
            info.serviceWorkerReady = 'available';
          } else {
            info.serviceWorkerReady = 'none';
          }
          console.log('[NotificationTest] Service Worker info:', { registrations: registrations.length });
        } catch (error) {
          console.error('[NotificationTest] Service Worker error:', error);
          info.serviceWorkerError = error instanceof Error ? error.message : String(error);
        }
      }

      // 检查通知 API 支持
      if ('Notification' in window) {
        try {
          info.notificationPermission = Notification.permission;
          info.notificationMaxActions = Notification.maxActions;
          console.log('[NotificationTest] Notification API info:', {
            permission: Notification.permission,
            maxActions: Notification.maxActions
          });
        } catch (error) {
          console.error('[NotificationTest] Notification API error:', error);
          info.notificationApiError = error instanceof Error ? error.message : String(error);
        }
      }

      // 检查 FCM 相关
      if (platformInfo?.isNative) {
        try {
          console.log('[NotificationTest] Checking FCM info...');
          const deviceId = localStorage.getItem('fcm_device_id');
          const deviceToken = localStorage.getItem('fcm_device_token');
          info.fcm = {
            deviceId: deviceId ? 'present' : 'missing',
            deviceToken: deviceToken ? 'present' : 'missing',
          };
          console.log('[NotificationTest] FCM info:', info.fcm);
        } catch (error) {
          console.error('[NotificationTest] FCM error:', error);
          info.fcmError = error instanceof Error ? error.message : String(error);
        }
      }

      // 检查本地存储
      try {
        console.log('[NotificationTest] Checking localStorage...');
        info.localStorage = {
          available: typeof Storage !== 'undefined',
        };

        // 安全地检查存储配额
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          try {
            const estimate = await navigator.storage.estimate();
            info.localStorage.quota = estimate;
            console.log('[NotificationTest] Storage estimate:', estimate);
          } catch (quotaError) {
            console.warn('[NotificationTest] Storage estimate failed:', quotaError);
            info.localStorage.quota = 'estimate_failed';
          }
        } else {
          info.localStorage.quota = 'not_supported';
        }
      } catch (error) {
        console.error('[NotificationTest] localStorage error:', error);
        info.localStorageError = error instanceof Error ? error.message : String(error);
      }

      console.log('[NotificationTest] Final diagnostic info:', info);
      setDiagnosticInfo(info);

      toast({
        title: '诊断信息已收集',
        description: '诊断信息已更新，请查看诊断面板',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('[NotificationTest] Error collecting diagnostic info:', error);
      toast({
        title: '收集诊断信息失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 获取权限文本描述
  const getPermissionText = (status: PermissionStatus): string => {
    switch (status) {
      case 'granted': return t('v0/notifications:permissionStatus.granted');
      case 'denied': return t('v0/notifications:permissionStatus.denied');
      case 'prompt': return t('v0/notifications:permissionStatus.prompt');
      default: return t('v0/notifications:permissionStatus.unknown');
    }
  };

  // 权限状态标签颜色
  const getPermissionColor = (status: PermissionStatus): string => {
    switch (status) {
      case 'granted': return 'green';
      case 'denied': return 'red';
      case 'prompt': return 'yellow';
      default: return 'gray';
    }
  };

  // 平台标签颜色
  const getPlatformColor = (platform: string): string => {
    switch (platform) {
      case 'ios': return 'blue';
      case 'android': return 'green';
      case 'web': return 'purple';
      case 'electron': return 'cyan';
      default: return 'gray';
    }
  };

  return (
    <PageLayout title={t('v0/common:notificationTest.title')}>
      <Container maxW="container.lg" py={4}>
        {/* 平台信息与测试指导 */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{t('v0/notifications:sections.platformInfo')}</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('v0/notificationTest:platformInfo.description')}
            </Text>
          </CardHeader>
          <CardBody>
            {platformInfo && (
              <VStack spacing={4} align="stretch">
                {/* 平台标签 */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:platformInfo.platformType')}:</Text>
                  <Flex gap={2} wrap="wrap">
                    <Badge colorScheme={getPlatformColor(platformInfo.os)} size="lg">
                      {platformInfo.os.toUpperCase()}
                    </Badge>
                    {platformInfo.isNative && <Badge colorScheme="green">{t('v0/notificationTest:platformInfo.nativeApp')}</Badge>}
                    {platformInfo.isWeb && <Badge colorScheme="purple">{t('v0/notificationTest:platformInfo.webBrowser')}</Badge>}
                    {platformInfo.isElectron && <Badge colorScheme="cyan">{t('v0/notificationTest:platformInfo.electron')}</Badge>}
                    {platformInfo.isPWA && <Badge colorScheme="orange">{t('v0/notificationTest:platformInfo.pwa')}</Badge>}
                    {platformInfo.isStandalone && <Badge colorScheme="teal">{t('v0/notificationTest:platformInfo.standaloneMode')}</Badge>}
                  </Flex>
                </Box>

                {/* 操作系统详情 */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:platformInfo.operatingSystem')}:</Text>
                  <Flex gap={2} wrap="wrap">
                    {platformInfo.isIOS && <Badge colorScheme="blue">{t('v0/notificationTest:platformInfo.ios')}</Badge>}
                    {platformInfo.isAndroid && <Badge colorScheme="green">{t('v0/notificationTest:platformInfo.android')}</Badge>}
                    {platformInfo.isMacOS && <Badge colorScheme="gray">{t('v0/notificationTest:platformInfo.macos')}</Badge>}
                    {platformInfo.isWindows && <Badge colorScheme="blue">{t('v0/notificationTest:platformInfo.windows')}</Badge>}
                    {platformInfo.isLinux && <Badge colorScheme="orange">{t('v0/notificationTest:platformInfo.linux')}</Badge>}
                  </Flex>
                </Box>

                {/* 通知支持情况 */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:platformInfo.notificationSupport')}:</Text>
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:platformInfo.localNotification')}</Text>
                      <Badge colorScheme="green">{t('v0/notificationTest:platformInfo.supported')}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:platformInfo.fcmPush')}</Text>
                      <Badge colorScheme={platformInfo.isNative ? "green" : "orange"}>
                        {platformInfo.isNative ? t('v0/notificationTest:platformInfo.fullySupported') : t('v0/notificationTest:platformInfo.limitedSupport')}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:platformInfo.webPush')}</Text>
                      <Badge colorScheme={platformInfo.isWeb && !platformInfo.isElectron ? "green" : "red"}>
                        {platformInfo.isWeb && !platformInfo.isElectron ? t('v0/notificationTest:platformInfo.supported') : t('v0/notificationTest:platformInfo.notSupported')}
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>

                {/* 测试建议 */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:platformInfo.testSuggestions')}:</Text>
                  <VStack spacing={1} align="stretch" fontSize="sm" color="gray.600">
                    {platformInfo.isNative && (
                      <>
                        <Text>{t('v0/notificationTest:platformInfo.testLocalNotifications')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.testFcmRegistration')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.testTaskReminders')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.checkPermissionStatus')}</Text>
                      </>
                    )}
                    {platformInfo.isWeb && !platformInfo.isElectron && (
                      <>
                        <Text>{t('v0/notificationTest:platformInfo.testBrowserNotifications')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.checkServiceWorker')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.testPwaNotifications')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.fcmNeedsConfig')}</Text>
                      </>
                    )}
                    {platformInfo.isElectron && (
                      <>
                        <Text>{t('v0/notificationTest:platformInfo.testElectronNotifications')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.checkSystemPermissions')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.fcmNotAvailableInElectron')}</Text>
                        <Text>{t('v0/notificationTest:platformInfo.testCrossPlatformConsistency')}</Text>
                      </>
                    )}
                  </VStack>
                </Box>

                {/* 详细信息（可折叠） */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:platformInfo.details')}:</Text>
                  <Box p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto">
                    <pre>{JSON.stringify(platformInfo, null, 2)}</pre>
                  </Box>
                </Box>
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* App 通知权限（本地和 FCM 共用） */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{t('v0/notifications:sections.notificationPermission')}</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('v0/notificationTest:platformInfo.sharedPermissionSettings')}
            </Text>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              <Flex alignItems="center" gap={4}>
                <Text>{t('v0/notifications:labels.currentPermissionStatus')}:</Text>
                <Badge colorScheme={getPermissionColor(permissionStatus)}>
                  {getPermissionText(permissionStatus)}
                </Badge>
              </Flex>
              <Flex gap={2}>
                <Button colorScheme="blue" onClick={checkPermissionStatus}>
                  {t('v0/notifications:buttons.checkPermission')}
                </Button>
                <Button
                  colorScheme={permissionStatus === 'granted' ? "green" : (platformInfo?.isElectron ? "orange" : "green")}
                  onClick={handleRequestPermissionClick}
                >
                  {permissionStatus === 'granted'
                    ? t('v0/notifications:buttons.permissionGranted')
                    : (platformInfo?.isElectron
                        ? t('v0/notifications:buttons.openSystemSettings')
                        : t('v0/notifications:buttons.requestPermission'))}
                </Button>
              </Flex>
              {(platformInfo?.isElectron && permissionStatus === 'granted') && (
                <Text fontSize="sm" color="gray.500">
                  {t('v0/notifications:hints.electronPermissionGranted')}
                </Text>
              )}
              {permissionStatus === 'denied' && (
                <Text fontSize="sm" color={useColorModeValue("red.600", "red.400")}>
                  {t('v0/notifications:hints.permissionDenied', {
                    hint: platformInfo?.isElectron
                      ? t('v0/notifications:hints.openSystemSettings')
                      : t('v0/notifications:hints.checkBrowserSettings')
                  })}
                </Text>
              )}
            </Stack>
          </CardBody>
        </Card>

        {/* 发送测试通知（本地渠道） */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{t('v0/notifications:sections.sendTestNotification')} ({t('v0/notificationTest:channels.local')})</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('v0/notificationTest:sendTestNotification.description')}
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('v0/notifications:labels.notificationTitle')}</FormLabel>
                <Input
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="输入通知标题"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('v0/notifications:labels.notificationContent')}</FormLabel>
                <Textarea
                  value={notificationBody}
                  onChange={(e) => setNotificationBody(e.target.value)}
                  placeholder="输入通知内容"
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('v0/notifications:labels.delaySend')}</FormLabel>
                <Input
                  type="number"
                  value={notificationDelay}
                  onChange={(e) => setNotificationDelay(parseInt(e.target.value, 10) || 0)}
                  min={0}
                  max={3600}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="sound-switch" mb={0}>
                  {t('v0/notifications:labels.enableSound')}
                </FormLabel>
                <Switch
                  id="sound-switch"
                  isChecked={notificationSound}
                  onChange={(e) => setNotificationSound(e.target.checked)}
                />
              </FormControl>

              <Button
                colorScheme="blue"
                onClick={sendTestNotification}
                isDisabled={permissionStatus !== 'granted'}
              >
                {t('v0/notifications:buttons.sendNotification')}
              </Button>
            </VStack>
          </CardBody>
        </Card>

        {/* FCM 推送测试 */}
        <FCMTestPanel />

        {/* 任务提醒测试 */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{t('v0/notifications:sections.taskReminderTest')}</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('v0/notifications:hints.taskReminderTest')}
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('v0/notifications:labels.taskTitle')}</FormLabel>
                <Input
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="输入任务标题"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('v0/notifications:labels.taskNotes')}</FormLabel>
                <Textarea
                  value={taskNotes}
                  onChange={(e) => setTaskNotes(e.target.value)}
                  placeholder="输入任务备注"
                  rows={2}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t('v0/notifications:labels.notificationChannel')}</FormLabel>
                <RadioGroup onChange={(val) => setReminderChannel(val as 'local' | 'fcm')} value={reminderChannel}>
                  <Stack direction="row">
                    <Radio value="local">
                      <HStack spacing={1}>
                        <Text>{t('v0/notifications:channels.local')}</Text>
                        <Badge colorScheme="blue" size="sm">{t('v0/notificationTest:channels.local')}</Badge>
                      </HStack>
                    </Radio>
                    <Radio value="fcm">
                      <HStack spacing={1}>
                        <Text>{t('v0/notifications:channels.fcm')}</Text>
                        <Badge colorScheme="green" size="sm">FCM</Badge>
                      </HStack>
                    </Radio>
                  </Stack>
                </RadioGroup>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {reminderChannel === 'local'
                    ? t('v0/notifications:hints.localChannelDescription')
                    : t('v0/notifications:hints.fcmChannelDescription')
                  }
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>{t('v0/notifications:labels.reminderType')}</FormLabel>
                <RadioGroup onChange={(val) => setReminderType(val as 'absolute' | 'relative')} value={reminderType}>
                  <Stack direction="row">
                    <Radio value="relative">{t('v0/notifications:labels.relativeTime')}</Radio>
                    <Radio value="absolute">{t('v0/notifications:labels.absoluteTime')}</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>

              {reminderType === 'absolute' ? (
                <FormControl>
                  <FormLabel>{t('v0/notifications:labels.reminderTime')}</FormLabel>
                  <Input
                    type="datetime-local"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                  />
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel>{t('v0/notifications:labels.delayTime')}</FormLabel>
                  <Input
                    type="number"
                    value={reminderOffset}
                    onChange={(e) => setReminderOffset(parseInt(e.target.value, 10) || 30)}
                    min={5}
                    max={3600}
                  />
                </FormControl>
              )}

              <Button
                colorScheme="purple"
                onClick={sendTaskReminder}
                isDisabled={permissionStatus !== 'granted' || (reminderChannel === 'fcm' && !platformInfo?.isNative)}
              >
                {t('v0/notifications:buttons.testTaskReminder')} ({reminderChannel === 'local' ? '本地' : 'FCM'})
              </Button>

              {reminderChannel === 'fcm' && !platformInfo?.isNative && (
                <Text fontSize="sm" color="orange.500">
                  {t('v0/notifications:hints.fcmRequiresNative')}
                </Text>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* 综合测试面板 */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Heading size="md">{t('v0/notificationTest:comprehensiveTest.title')}</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              {t('v0/notificationTest:comprehensiveTest.description')}
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="semibold">{t('v0/notificationTest:comprehensiveTest.quickTestScenarios')}:</Text>

              <HStack spacing={2} wrap="wrap">
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => {
                    // 测试即时本地通知
                    sendTestNotification();
                  }}
                  isDisabled={permissionStatus !== 'granted'}
                >
                  {t('v0/notificationTest:comprehensiveTest.instantLocalNotification')}
                </Button>

                <Button
                  size="sm"
                  colorScheme="orange"
                  onClick={() => {
                    // 测试延迟本地通知
                    const originalDelay = notificationDelay;
                    setNotificationDelay(5);
                    setTimeout(() => {
                      sendTestNotification();
                      setNotificationDelay(originalDelay);
                    }, 100);
                  }}
                  isDisabled={permissionStatus !== 'granted'}
                >
                  {t('v0/notificationTest:comprehensiveTest.delayedLocalNotification')}
                </Button>

                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={() => {
                    // 测试本地任务提醒
                    const originalChannel = reminderChannel;
                    const originalOffset = reminderOffset;
                    setReminderChannel('local');
                    setReminderOffset(10);
                    setTimeout(() => {
                      sendTaskReminder();
                      setReminderChannel(originalChannel);
                      setReminderOffset(originalOffset);
                    }, 100);
                  }}
                  isDisabled={permissionStatus !== 'granted'}
                >
                  {t('v0/notificationTest:comprehensiveTest.localTaskReminder')}
                </Button>

                {platformInfo?.isNative && (
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={() => {
                      // 测试 FCM 任务提醒
                      const originalChannel = reminderChannel;
                      const originalOffset = reminderOffset;
                      setReminderChannel('fcm');
                      setReminderOffset(15);
                      setTimeout(() => {
                        sendTaskReminder();
                        setReminderChannel(originalChannel);
                        setReminderOffset(originalOffset);
                      }, 100);
                    }}
                    isDisabled={permissionStatus !== 'granted'}
                  >
                    {t('v0/notificationTest:comprehensiveTest.fcmTaskReminder')}
                  </Button>
                )}
              </HStack>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:comprehensiveTest.testChecklist')}:</Text>
                <VStack spacing={1} align="stretch" fontSize="sm">
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.checkmark')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.permissionStatusCheck')}</Text>
                  </HStack>
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.instantLocalNotificationDisplay')}</Text>
                  </HStack>
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.delayedLocalNotificationScheduling')}</Text>
                  </HStack>
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.localTaskReminderFunction')}</Text>
                  </HStack>
                  {platformInfo?.isNative && (
                    <>
                      <HStack>
                        <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                        <Text>{t('v0/notificationTest:comprehensiveTest.fcmDeviceRegistration')}</Text>
                      </HStack>
                      <HStack>
                        <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                        <Text>{t('v0/notificationTest:comprehensiveTest.fcmPushReceiving')}</Text>
                      </HStack>
                      <HStack>
                        <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                        <Text>{t('v0/notificationTest:comprehensiveTest.fcmTaskReminders')}</Text>
                      </HStack>
                    </>
                  )}
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.notificationLogging')}</Text>
                  </HStack>
                  <HStack>
                    <Text>{t('v0/notificationTest:comprehensiveTest.unchecked')}</Text>
                    <Text>{t('v0/notificationTest:comprehensiveTest.notificationInteraction')}</Text>
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* 通知日志 */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Flex justify="space-between" align="center">
              <Box>
                <Heading size="md">{t('v0/notifications:sections.notificationLog')}</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {t('v0/notifications:hints.notificationLogDescription')}
                </Text>
              </Box>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNotificationLog([])}
                isDisabled={notificationLog.length === 0}
              >
                {t('v0/notifications:buttons.clearLog')}
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto">
              {notificationLog.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500" mb={2}>{t('v0/notifications:hints.noNotificationRecords')}</Text>
                  <Text fontSize="sm" color="gray.400">
                    {t('v0/notifications:hints.notificationLogInstructions')}
                  </Text>
                </Box>
              ) : (
                notificationLog.map((notification, index) => (
                  <Box key={index} p={3} borderRadius="md" bg={logBg} border="1px solid" borderColor={useColorModeValue('gray.200', 'gray.600')}>
                    <Flex justify="space-between" align="start" mb={1}>
                      <Text fontWeight="bold" flex={1}>{notification.title}</Text>
                      <Badge
                        colorScheme={
                          notification.title.includes('[ACTION]') ? 'green' :
                          notification.title.includes('[SENT]') ? 'blue' :
                          notification.title.includes('[SCHEDULED]') ? 'orange' :
                          notification.title.includes('[TASK REMINDER]') ? 'purple' :
                          'gray'
                        }
                        size="sm"
                      >
                        {
                          notification.title.includes('[ACTION]') ? 'ACTION' :
                          notification.title.includes('[SENT]') ? 'SENT' :
                          notification.title.includes('[SCHEDULED]') ? 'SCHEDULED' :
                          notification.title.includes('[TASK REMINDER]') ? 'TASK' :
                          'RECEIVED'
                        }
                      </Badge>
                    </Flex>
                    <Text fontSize="sm" mb={2}>{notification.body}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {t('v0/notificationTest:notificationLog.idLabel')}: {notification.id}
                      {notification.actionId && ` | ${t('v0/notificationTest:notificationLog.actionLabel')}: ${notification.actionId}`}
                      {notification.extra && ` | ${t('v0/notificationTest:notificationLog.typeLabel')}: ${notification.extra.type || t('v0/notificationTest:notificationLog.unknown')}`}
                    </Text>
                  </Box>
                ))
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* 诊断信息面板 */}
        <Card mb={4} bg={cardBg}>
          <CardHeader pb={0}>
            <Flex justify="space-between" align="center">
              <Box>
                <Heading size="md">{t('v0/notificationTest:diagnostics.title')}</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {t('v0/notificationTest:diagnostics.description')}
                </Text>
              </Box>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={collectDiagnosticInfo}
                >
                  {t('v0/notificationTest:diagnostics.collectButton')}
                </Button>
                {/* <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    console.log('[NotificationTest] Test button clicked');
                    alert('测试按钮被点击了！');
                  }}
                >
                  测试按钮
                </Button> */}
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            {diagnosticInfo ? (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:diagnostics.environmentCheck')}:</Text>
                  <VStack spacing={1} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:diagnostics.serviceWorkerSupport')}</Text>
                      <Badge colorScheme={diagnosticInfo.serviceWorker ? "green" : "red"}>
                        {diagnosticInfo.serviceWorker ? "✓" : "✗"}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:diagnostics.notificationApi')}</Text>
                      <Badge colorScheme={diagnosticInfo.notifications ? "green" : "red"}>
                        {diagnosticInfo.notifications ? "✓" : "✗"}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:diagnostics.pushManager')}</Text>
                      <Badge colorScheme={diagnosticInfo.pushManager ? "green" : "red"}>
                        {diagnosticInfo.pushManager ? "✓" : "✗"}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:diagnostics.localStorage')}</Text>
                      <Badge colorScheme={diagnosticInfo.localStorage?.available ? "green" : "red"}>
                        {diagnosticInfo.localStorage?.available ? "✓" : "✗"}
                      </Badge>
                    </HStack>
                    {diagnosticInfo.serviceWorkerRegistrations !== undefined && (
                      <HStack justify="space-between">
                        <Text>{t('v0/notificationTest:diagnostics.swRegistrationCount')}</Text>
                        <Badge colorScheme={diagnosticInfo.serviceWorkerRegistrations > 0 ? "green" : "orange"}>
                          {diagnosticInfo.serviceWorkerRegistrations}
                        </Badge>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                {diagnosticInfo.fcm && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:diagnostics.fcmStatus')}:</Text>
                    <VStack spacing={1} align="stretch" fontSize="sm">
                      <HStack justify="space-between">
                        <Text>{t('v0/notificationTest:diagnostics.deviceId')}</Text>
                        <Badge colorScheme={diagnosticInfo.fcm.deviceId === 'present' ? "green" : "orange"}>
                          {diagnosticInfo.fcm.deviceId}
                        </Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>{t('v0/notificationTest:diagnostics.deviceToken')}</Text>
                        <Badge colorScheme={diagnosticInfo.fcm.deviceToken === 'present' ? "green" : "orange"}>
                          {diagnosticInfo.fcm.deviceToken}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:diagnostics.permissionStatus')}:</Text>
                  <VStack spacing={1} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text>{t('v0/notificationTest:diagnostics.currentPermission')}</Text>
                      <Badge colorScheme={getPermissionColor(diagnosticInfo.permission)}>
                        {getPermissionText(diagnosticInfo.permission)}
                      </Badge>
                    </HStack>
                    {diagnosticInfo.notificationPermission && (
                      <HStack justify="space-between">
                        <Text>{t('v0/notificationTest:diagnostics.notificationPermission')}</Text>
                        <Badge colorScheme={diagnosticInfo.notificationPermission === 'granted' ? "green" : "orange"}>
                          {diagnosticInfo.notificationPermission}
                        </Badge>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('v0/notificationTest:diagnostics.fullDiagnosticData')}:</Text>
                  <Box p={3} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md" fontSize="xs" fontFamily="monospace" overflowX="auto" maxH="200px" overflowY="auto">
                    <pre>{JSON.stringify(diagnosticInfo, null, 2)}</pre>
                  </Box>
                </Box>
              </VStack>
            ) : (
              <Box textAlign="center" py={8}>
                <Text color="gray.500" mb={4}>{t('v0/notificationTest:diagnostics.clickToCollect')}</Text>
                <Text fontSize="sm" color="gray.400">
                  {t('v0/notificationTest:diagnostics.helpText')}
                </Text>
              </Box>
            )}
          </CardBody>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default NotificationTest;